<script setup>
import AWSTable from '@/components/Cloud/Dashboard/Components/aws-table.vue';
import { functionTableHeader } from '@/components/Cloud/Dashboard/pages/lambda/lambda.types';
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStoreData } from '@/components/Common/useStoreData';
import { useStoreDataCloud } from '@/components/Cloud/Dashboard/useStoreData';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import { convertDateFormat } from '@/utils/helper-function';

const { getDurationTime } = useStoreData();
const {
  fetchLogsData,
  setCurrentPage,
  getCurrentServiceInstances,
  getTimeZone,
} = useStoreDataCloud();
const route = useRoute();
const logData = ref([]);
const isLoading = ref(false);
const totalLogs = ref(0);

const getLogsData = async (pageNum, pageSize) => {
  try {
    isLoading.value = true;
    const response = await fetchLogsData({
      condition: {
        serviceInstanceId: getCurrentServiceInstances.value[0],
      },
      duration: getDurationTime.value,
      pagination: {
        pageNum: pageNum,
        pageSize: pageSize,
        needTotal: true,
      },
    });
    logData.value = response.data.getLambdaFunctionsLogs.logs;
    totalLogs.value = response.data.getLambdaFunctionsLogs.total;
    const reg = /\(GMT[+-]\d{2}:\d{2}\)/;
    const matchResult = getTimeZone.value.match(reg);
    const timeZone = matchResult ? matchResult[0] : '(GMT)';
    logData.value.forEach((invocation) => {
      invocation.timestamp = `${convertDateFormat(invocation.timestamp, 'YYYY-MM-DD HH:mm:ss')} ${timeZone}`;
    });
    isLoading.value = false;
  } catch (error) {
    console.error('Error fetching logs data:', error);
    isLoading.value = false;
  }
};

onMounted(async () => {
  setCurrentPage('functions');
  getLogsData();
});
const breadcrumbData = computed(() => {
  return route.params.databaseId.split('/');
});

const getPdfTitle = computed(
  () =>
    route.params.databaseId.split('/')[3] + '/' + route.params.subscriptionId,
);
</script>

<template>
  <div class="breadcrumb">
    <h4 class="overview-text breadcrumb-first">
      <router-link :to="{ name: 'awsoverview' }" class="breadcrumb-link">
        {{ $t('aws.overviewText') }}
      </router-link>
    </h4>
    <h4 class="overview-text breadcrumb-first">
      <router-link
        :to="{
          path: `/cloud/aws/lambda/${breadcrumbData[1]}/${breadcrumbData[2]}`,
        }"
        class="breadcrumb-link"
      >
        {{ breadcrumbData[1] }} >
      </router-link>
    </h4>
    <h4 class="overview-text">
      {{ breadcrumbData[3] }}
    </h4>
  </div>
  <RexSpinV3 :spinning="isLoading">
    <AWSTable
      :placeholder="$t('aws.searchRequestId')"
      :title="$t('aws.invocations')"
      :pdfTitle="getPdfTitle"
      :headers="functionTableHeader()"
      :data="logData"
      tableFilterField="requestId"
      :showTooltip="true"
      :paginationFunction="getLogsData"
      :enablePagination="true"
      :totalRecords="totalLogs"
    />
  </RexSpinV3>
</template>

<style lang="scss" scoped>
.breadcrumb {
  display: flex;
  background: inherit;
  gap: 0.3125rem;
}

.breadcrumb-first {
  cursor: pointer;
}

.breadcrumb-link:hover {
  text-decoration: none;
  color: inherit;
  color: $color-brand-accent;
}

.breadcrumb-link {
  color: $color-brand-accent;
  text-decoration: none;
}

.pagination {
  display: flex;
  justify-content: end;
  padding-top: 10px;
}
</style>
